import { useEffect, useMemo, useRef } from 'react';
import { useSavedSearches } from 'features/saved-searches';
import { savePreferences } from 'services/FiltersStorage';
import { loadFromLocalStorage } from 'UI/utils';

import { RESUME_SEARCH_SAVED_SEARCHES_PREFERENCES_KEY } from '../ResumeSearch.constants';

const DEFAULT_SAVED_SEARCH_PREFERENCES = {
  filters: {},
  savedSearch: null
};

export const useResumeSearchSavedSearches = ({ filters = {}, onAutoApplySearch } = {}) => {
  useEffect(() => {
    const storedPreferences = loadFromLocalStorage(RESUME_SEARCH_SAVED_SEARCHES_PREFERENCES_KEY);

    if (storedPreferences === null) {
      savePreferences(
        RESUME_SEARCH_SAVED_SEARCHES_PREFERENCES_KEY,
        DEFAULT_SAVED_SEARCH_PREFERENCES
      );
    }
  }, []);

  const lastAutoAppliedSearchIdRef = useRef(null);
  const savedSearchesState = useSavedSearches({
    preferencesKey: RESUME_SEARCH_SAVED_SEARCHES_PREFERENCES_KEY
  });

  const {
    activeSearch,
    isDirty,
    savedSearchesUiState,
    getSavedSearchButtonDefinitions,
    handleApplySearch,
    handleDeleteSearch,
    handleEditSearch,
    handleRemoveSearch,
    handleSavedSearchDrawerOpen,
    handleCreationSavedSearchDrawerOpen,
    handleEditSavedSearchDrawerOpen,
    handleCloseSavedSearchDrawer,
    handleCloseCreationSavedSearchDrawer,
    handleCloseEditSavedSearchDrawer,
    handleCloseSavedSearchForm
  } = savedSearchesState;

  useEffect(() => {
    const activeSearchId = activeSearch?.id;

    if (typeof onAutoApplySearch !== 'function') return;

    if (activeSearchId == null) {
      lastAutoAppliedSearchIdRef.current = null;
      return;
    }

    if (lastAutoAppliedSearchIdRef.current === activeSearchId) {
      return;
    }

    onAutoApplySearch(activeSearch);
    lastAutoAppliedSearchIdRef.current = activeSearchId;
  }, [activeSearch, onAutoApplySearch]);

  const buttonDefinitions = useMemo(() => {
    return getSavedSearchButtonDefinitions({ filters });
  }, [filters, getSavedSearchButtonDefinitions]);

  return {
    activeSearch,
    isDirty,
    savedSearchesUiState,
    buttonDefinitions,
    handleApplySearch,
    handleDeleteSearch,
    handleEditSearch,
    handleRemoveSearch,
    handleSavedSearchDrawerOpen,
    handleCreationSavedSearchDrawerOpen,
    handleEditSavedSearchDrawerOpen,
    handleCloseSavedSearchDrawer,
    handleCloseCreationSavedSearchDrawer,
    handleCloseEditSavedSearchDrawer,
    handleCloseSavedSearchForm
  };
};
